import { usePrincipalKey } from "@auth/sessionStore";
import { useEmailMessage } from "@email/emailQueries";
import { subscribeToFrames } from "@notifications/notificationFrames";
import { Button, PaperPanel } from "@scribenote/brian-design-system";
import { useOpenEmail } from "@shell/navigation";
import { useEffect, useState } from "react";

export const IncomingEmailNotice = () => {
  const principal = usePrincipalKey();
  return <EmailNotice key={principal} principal={principal} />;
};

const EmailNotice = ({ principal }: { principal: string }) => {
  const [queue, setQueue] = useState<string[]>([]);
  const selected = queue[0];
  const message = useEmailMessage(principal, selected);
  const openEmail = useOpenEmail();
  useEffect(() => {
    const seen = new Set<string>();
    return subscribeToFrames((events) => {
      const arrivals = events
        .filter(
          (event) =>
            event.type === "email_message" &&
            event.uuid &&
            !seen.has(event.uuid),
        )
        .map((event) => event.uuid!);
      for (const uuid of arrivals) seen.add(uuid);
      while (seen.size > 200) seen.delete(seen.values().next().value!);
      if (arrivals.length)
        setQueue((current) =>
          [...new Set([...current, ...arrivals])].slice(-20),
        );
    });
  }, []);
  const dismiss = () => setQueue((current) => current.slice(1));
  if (!selected) return null;
  return (
    <PaperPanel
      className="w-80 max-w-[calc(100vw-2rem)] p-4 shadow-lg"
      data-dd-privacy="hidden"
    >
      <div role="status" className="space-y-1">
        <p className="font-medium">
          New email{queue.length > 1 ? ` · ${queue.length} unread alerts` : ""}
        </p>
        {message.data && (
          <>
            <p className="break-words text-sm">{message.data.sender}</p>
            <p className="text-sm text-annotation">
              {message.data.subject || "(No subject)"}
            </p>
            {message.data.association?.client && (
              <p className="text-xs text-annotation">
                Client: {message.data.association.client.name}
              </p>
            )}
          </>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          onClick={() => {
            openEmail({ message: selected });
            dismiss();
          }}
        >
          Open email
        </Button>
        <Button size="sm" variant="ghost" onClick={dismiss}>
          Dismiss
        </Button>
      </div>
    </PaperPanel>
  );
};
